import React, { useState, useEffect } from "react";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ThinTextField, ThinDropdown } from "../common/GeneralComponent";

const EditHouseholdDebt = ({ householdDebt, DqaApproved, DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    hasDebt: "",
    loanSources: [],
    otherLoanSource: "",
    debtPaidStatus: "",
    loanAmount: "",
    typeOfLoan: "",
    loanInterestAmount: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [debtRecord, setDebtRecord] = useState(null);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);

  // Check if there's any debt data
  const hasDebtData = householdDebt && Object.keys(householdDebt).length > 0;

  useEffect(() => {
    setIsDqaApproved(DqaApproved);
    setIsDqaRejected(DqaRejected);
  }, [DqaApproved, DqaRejected]);

  // Init from props
  useEffect(() => {
    if (hasDebtData) {
      setDebtRecord(householdDebt);

      const loanSources = [];
      if (householdDebt.loan_from_relatives_friends)
        loanSources.push("relatives");
      if (householdDebt.loan_from_commercial_bank) loanSources.push("bank");
      if (householdDebt.loan_from_microfinance)
        loanSources.push("microfinance");
      if (householdDebt.loan_from_informal_lenders)
        loanSources.push("informal");
      if (householdDebt.loan_from_other_sources) loanSources.push("others");

      setFormData({
        hasDebt:
          householdDebt.has_outstanding_loans === 1 ||
          householdDebt.has_outstanding_loans === true
            ? "yes"
            : "no",
        loanSources,
        otherLoanSource: householdDebt.loan_from_other_specify || "",
        debtPaidStatus: householdDebt.debt_repaid_status
          ? String(householdDebt.debt_repaid_status)
          : "",
        loanAmount: householdDebt.loan_amount ?? "",
        typeOfLoan: householdDebt.type_of_loan ?? "",
        loanInterestAmount: householdDebt.loan_interest_amount ?? "",
      });
    } else {
      // new/empty
      setFormData({
        hasDebt: "",
        loanSources: [],
        otherLoanSource: "",
        debtPaidStatus: "",
        loanAmount: "",
        typeOfLoan: "",
        loanInterestAmount: "",
      });
    }
    setLoading(false);
  }, [householdDebt, id, hasDebtData]);

  // Helpers
  const isNumberLike = (v) => v !== "" && !isNaN(Number(v));
  const nonNeg = (v) => Number(v) >= 0;

  const setField = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      const next = checked
        ? [...formData.loanSources, value]
        : formData.loanSources.filter((v) => v !== value);
      setField("loanSources", next);
      if (value !== "others") {
        setField("otherLoanSource", formData.otherLoanSource);
      }
      return;
    }

    setField(name, value);

    // If radio toggles to "no", clear dependent fields & errors
    if (name === "hasDebt" && value === "no") {
      setFormData((p) => ({
        ...p,
        loanSources: [],
        otherLoanSource: "",
        debtPaidStatus: "",
        loanAmount: "",
        typeOfLoan: "",
        loanInterestAmount: "",
      }));
      setErrors({});
    }
  };

  const validate = () => {
    const e = {};
    const req = (v) => v !== null && v !== undefined && String(v).trim() !== "";

    // hasDebt required
    if (!req(formData.hasDebt)) e.hasDebt = "Please select Yes or No.";

    if (formData.hasDebt === "yes") {
      // Loan sources
      if (!formData.loanSources || formData.loanSources.length === 0) {
        e.loanSources = "Select at least one source.";
      }
      if (
        formData.loanSources.includes("others") &&
        !req(formData.otherLoanSource)
      ) {
        e.otherLoanSource = "Please specify other source.";
      }

      // Loan details
      if (!req(formData.loanAmount)) {
        e.loanAmount = "Loan  Amount is Required.";
      } else if (
        !isNumberLike(formData.loanAmount) ||
        !nonNeg(formData.loanAmount)
      ) {
        e.loanAmount = "Enter a non-negative number.";
      }

      if (!req(formData.typeOfLoan)) {
        e.typeOfLoan = "Type of Loan is Required.";
      }

      if (formData.typeOfLoan === "With interest") {
        if (!req(formData.loanInterestAmount)) {
          e.loanInterestAmount = "Loan Interest Amount is required.";
        } else if (
          !isNumberLike(formData.loanInterestAmount) ||
          !nonNeg(formData.loanInterestAmount)
        ) {
          e.loanInterestAmount = "Enter a non-negative number.";
        }
      }

      // Debt paid status
      if (!req(formData.debtPaidStatus)) {
        e.debtPaidStatus = "Please select one option.";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdate = async () => {
    if (!validate()) {
      return;
    }

    try {
      setUpdating(true);

      const payload = {
        household_information_id: parseInt(id),
        has_outstanding_loans: formData.hasDebt === "yes" ? 1 : 0,
        loan_from_relatives_friends: formData.loanSources.includes("relatives")
          ? 1
          : 0,
        loan_from_commercial_bank: formData.loanSources.includes("bank")
          ? 1
          : 0,
        loan_from_microfinance: formData.loanSources.includes("microfinance")
          ? 1
          : 0,
        loan_from_informal_lenders: formData.loanSources.includes("informal")
          ? 1
          : 0,
        loan_from_other_sources: formData.loanSources.includes("others")
          ? 1
          : 0,
        loan_from_other_specify: formData.loanSources.includes("others")
          ? formData.otherLoanSource || null
          : null,
        debt_repaid_status:
          formData.hasDebt === "yes" && formData.debtPaidStatus
            ? formData.debtPaidStatus
            : null,
        loan_amount:
          formData.hasDebt === "yes" && formData.loanAmount !== ""
            ? formData.loanAmount
            : null,
        type_of_loan:
          formData.hasDebt === "yes" && formData.typeOfLoan
            ? formData.typeOfLoan
            : null,
        loan_interest_amount:
          formData.hasDebt === "yes" && formData.loanInterestAmount !== ""
            ? formData.loanInterestAmount
            : null,
      };

      if (debtRecord && debtRecord.household_debt_id) {
        const response = await fetchData(
          `household-debt-update/${debtRecord.household_debt_id}`,
          "PUT",
          payload,
          {},
          {},
          navigate,
          "/household",
          dispatch
        );

        if (response?.success) {
          toast.success("Household debt updated successfully");
        } else {
          throw new Error(
            response?.message || "Failed to update household debt"
          );
        }
      } else {
        toast.info(
          "No existing debt record found. You may need to create a new record."
        );
        return;
      }
    } catch (error) {
      console.error("Error updating household debt:", error);
      toast.error(error.message || "Error updating household debt");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">
          Loading household debt information...
        </div>
      </div>
    );
  }

  // If no debt data, show message similar to Education component
  if (!hasDebtData) {
    return (
      <div className="bg-white">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            {isDqaApproved && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                Approved from DQA
              </span>
            )}
          </div>
        </div>

        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No household debt information available
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Debit
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Debit
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      <div className="space-y-6">
        {/* HD1 */}
        <div className="mb-2">
          <label className="block font-medium mb-2">
            Did any of your household members have any outstanding loans during
            the past 3 years?
          </label>
          <div className="flex gap-4 mt-1">
            <label className="flex items-center">
              <input
                type="radio"
                name="hasDebt"
                value="yes"
                checked={formData.hasDebt === "yes"}
                onChange={handleChange}
                className="mr-2"
                disabled={isDqaApproved || isDqaRejected}
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="hasDebt"
                value="no"
                checked={formData.hasDebt === "no"}
                onChange={handleChange}
                className="mr-2"
                disabled={isDqaApproved || isDqaRejected}
              />
              No
            </label>
          </div>
          {errors.hasDebt && (
            <p className="text-[0.9rem] text-red-600 mt-2">{errors.hasDebt}</p>
          )}
        </div>

        {/* HD3 */}
        {formData.hasDebt === "yes" && (
          <>
            <div className="mb-2">
              <label className="block font-medium mb-2">
                Where did the household obtain the loan from? (Multiple answers
                are allowed)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {[
                  {
                    label: "Relatives/friends/neighbors",
                    value: "relatives",
                  },
                  { label: "Commercial Bank", value: "bank" },
                  {
                    label: "Micro Finance Institutions",
                    value: "microfinance",
                  },
                  {
                    label:
                      "Informal Money lenders (arhti/beopari/landlords/shopkeepers",
                    value: "informal",
                  },
                  { label: "Others", value: "others" },
                ].map((source) => (
                  <label key={source.value} className="flex items-center">
                    <input
                      type="checkbox"
                      name="loanSources"
                      value={source.value}
                      checked={formData.loanSources.includes(source.value)}
                      onChange={handleChange}
                      className="mr-2"
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    {source.label}
                  </label>
                ))}
              </div>
              {errors.loanSources && (
                <p className="text-[0.9rem] text-red-600 mt-2">
                  {errors.loanSources}
                </p>
              )}

              {formData.loanSources.includes("others") && (
                <div className="mt-3">
                  <ThinTextField
                    isRequired={false}
                    type="text"
                    name="otherLoanSource"
                    value={formData.otherLoanSource}
                    onChange={handleChange}
                    placeholder="Specify other loan source"
                    className="border border-gray-300 p-2 rounded w-full max-w-md"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  {errors.otherLoanSource && (
                    <p className="text-[0.9rem] text-red-600 mt-2">
                      {errors.otherLoanSource}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Loan Details */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg ">
              <h3 className="text-lg font-medium mb-4">Loan Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
                <div>
                  <ThinTextField
                    label="Loan Amount"
                    isRequired={false}
                    type="text"
                    name="loanAmount"
                    value={formData.loanAmount}
                    onChange={handleChange}
                    placeholder="Enter loan amount"
                    className="border border-gray-300 p-2 rounded w-full"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  {errors.loanAmount && (
                    <p className="text-[0.9rem] text-red-600 mt-2">
                      {errors.loanAmount}
                    </p>
                  )}
                </div>

                <div>
                  <ThinDropdown
                    label="Type of Loan"
                    options={[
                      { label: "Personal", value: "Personal" },
                      { label: "Interest-free", value: "Interest-free" },
                      { label: "With interest", value: "With interest" },
                    ]}
                    name="typeOfLoan"
                    value={formData.typeOfLoan}
                    onChange={handleChange}
                    error={errors.typeOfLoan}
                    disabled={isDqaApproved || isDqaRejected}
                    required={true}
                    className="py-[0.45rem] border border-gray-300 p-2 rounded w-full"
                  />
                  {errors.typeOfLoan && (
                    <p className="text-[0.9rem] text-red-600 mt-2">
                      {errors.typeOfLoan}
                    </p>
                  )}
                </div>

                {formData.typeOfLoan === "With interest" && (
                  <div className="mb-6">
                    <ThinTextField
                      label="Loan Interest Amount"
                      isRequired={false}
                      type="text"
                      name="loanInterestAmount"
                      value={formData.loanInterestAmount}
                      onChange={handleChange}
                      placeholder="Enter loan interest amount"
                      className="border border-gray-300 p-2 rounded w-full"
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    {errors.loanInterestAmount && (
                      <p className="text-[0.9rem] text-red-600 mt-2">
                        {errors.loanInterestAmount}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* HD4 */}
            <div className="mb-2">
              <label className="block font-medium mb-2">
                Was the debt paid back?
              </label>

              <ThinDropdown
                label="Debt Paid Status"
                options={[
                  { label: "Yes, wholly", value: "Yes, wholly" },
                  { label: "Yes, Partly", value: "Yes, Partly" },
                  { label: "No", value: "No" },
                ]}
                name="debtPaidStatus"
                value={formData.debtPaidStatus} // Ensure this value corresponds to the options' value
                onChange={handleChange}
                error={errors.debtPaidStatus} // Bind the error for this field
                disabled={isDqaApproved || isDqaRejected} // Make dropdown disabled if DQA is approved
                required={true}
                className="w-[150px]"
              />

              {errors.debtPaidStatus && (
                <p className="text-[0.9rem] text-red-600 mt-2">
                  {errors.debtPaidStatus}
                </p>
              )}
            </div>
          </>
        )}
      </div>
      {!isDqaApproved && !isDqaRejected && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={updating || !debtRecord}
            className={`${
              updating || !debtRecord
                ? "bg-gray-400"
                : "bg-teal-600 hover:bg-teal-700"
            } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
          >
            {updating
              ? "Updating..."
              : !debtRecord
                ? "No Record to Update"
                : "Update"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EditHouseholdDebt;
